import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, BellRing, Award, Save, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminTitle, DataTable } from "@/pages/admin/AdminLayout";
import { Spinner, Empty } from "@/components/site/Bits";
import { useApi, useDownload, errMsg, fmtDate, fmtTime } from "@/lib/api";

const MeetingForm = ({ course, onSaved }) => {
  const api = useApi();
  const [m, setM] = useState({ meet_link: course.meet_link || "", meet_time: course.meet_time || course.time });
  useEffect(() => {
    setM({ meet_link: course.meet_link || "", meet_time: course.meet_time || course.time });
  }, [course]);
  const run = async (fn, ok) => {
    try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r) : ok); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-lux !transform-none p-6 grid md:grid-cols-12 gap-4 items-end mb-6" data-testid="meeting-form">
      <div className="md:col-span-6 space-y-1.5"><Label className="text-xs">رابط Google Meet</Label><Input dir="ltr" value={m.meet_link} onChange={(e) => setM({ ...m, meet_link: e.target.value })} placeholder="https://meet.google.com/xxx-xxxx-xxx" data-testid="meeting-link-input" /></div>
      <div className="md:col-span-2 space-y-1.5"><Label className="text-xs">وقت الاجتماع</Label><Input type="time" value={m.meet_time} onChange={(e) => setM({ ...m, meet_time: e.target.value })} data-testid="meeting-time-input" /></div>
      <div className="md:col-span-4 flex flex-wrap gap-2">
        <button className="btn-copper !py-2.5 !px-4 text-sm" onClick={() => run(() => api("put", `/admin/courses/${course.id}/meeting`, m), "تم حفظ رابط الاجتماع")} data-testid="meeting-save-btn"><Save size={15} />حفظ</button>
        <button className="btn-gold-outline !py-2.5 !px-4 text-sm" onClick={() => run(() => api("post", `/admin/courses/${course.id}/send-link`), (r) => `تم إرسال الرابط إلى ${r.sent} متدرب`)} data-testid="meeting-send-link-btn"><Send size={15} />إرسال الرابط</button>
        <button className="btn-gold-outline !py-2.5 !px-4 text-sm" onClick={() => run(() => api("post", `/admin/courses/${course.id}/send-reminder`), (r) => `تم إرسال التذكير إلى ${r.sent} متدرب`)} data-testid="meeting-send-reminder-btn"><BellRing size={15} />إرسال تذكير</button>
      </div>
    </div>
  );
};

export default function Meetings() {
  const api = useApi();
  const download = useDownload();
  const [courses, setCourses] = useState(null);
  const [cid, setCid] = useState("");
  const [roster, setRoster] = useState(null);
  const loadCourses = useCallback(() => api("get", "/admin/courses").then((l) => { setCourses(l); setCid((x) => x || l[0]?.id || ""); }), [api]);
  const loadRoster = useCallback(() => cid && api("get", `/admin/courses/${cid}/roster`).then(setRoster), [api, cid]);
  useEffect(() => {
    loadCourses();
  }, [loadCourses]);
  useEffect(() => {
    loadRoster();
  }, [loadRoster]);
  const act = async (fn, ok) => {
    try { const r = await fn(); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); loadRoster(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!courses) return <Spinner />;
  const course = courses.find((c) => c.id === cid);
  return (
    <div data-testid="admin-meetings">
      <AdminTitle title="الاجتماعات التدريبية والحضور والشهادات">
        <Select value={cid} onValueChange={setCid} dir="rtl">
          <SelectTrigger className="w-80 bg-white" data-testid="meetings-course-select"><SelectValue placeholder="اختر دورة" /></SelectTrigger>
          <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title.slice(0, 60)}</SelectItem>)}</SelectContent>
        </Select>
      </AdminTitle>
      {!course ? <Empty text="لا توجد دورات" /> : (
        <>
          <p className="text-sm text-[var(--muted-ink)] mb-4">موعد الدورة: {fmtDate(course.date)} — {fmtTime(course.time)}</p>
          <MeetingForm course={course} onSaved={loadCourses} />
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg">المتدربون المؤكدون ({roster?.length ?? 0})</h2>
            <button className="btn-copper !py-2 !px-4 text-sm" onClick={() => act(() => api("post", `/admin/courses/${cid}/certificates`), (r) => `تم إصدار ${r.issued} شهادة`)} data-testid="issue-all-certs-btn"><Award size={15} />إصدار شهادات لجميع الحاضرين</button>
          </div>
          {!roster ? <Spinner /> : !roster.length ? <Empty text="لا يوجد متدربون مؤكدون في هذه الدورة بعد" /> : (
            <DataTable head={["رقم التسجيل", "الاسم", "الهاتف", "الحضور", "الشهادة"]} testId="roster-table">
              {roster.map((e) => (
                <tr key={e.id} data-testid={`roster-row-${e.reg_no}`}>
                  <td className="font-mono text-xs" dir="ltr">{e.reg_no}</td><td>{e.full_name}</td><td dir="ltr" className="text-right">{e.phone}</td>
                  <td className="whitespace-nowrap">
                    {["present", "absent"].map((a) => (
                      <button key={a} onClick={() => act(() => api("put", `/admin/enrollments/${e.id}/attendance`, { attendance: a }))} data-testid={`attendance-${a}-${e.reg_no}`}
                        className={`text-xs px-3 py-1 rounded-full ml-1 border ${e.attendance === a ? (a === "present" ? "bg-emerald-600 text-white border-emerald-600" : "bg-red-600 text-white border-red-600") : "border-[var(--line)]"}`}>{a === "present" ? "حاضر" : "غائب"}</button>
                    ))}
                  </td>
                  <td>
                    {e.certificate_no ? (
                      <button className="text-[var(--copper)] text-xs inline-flex items-center gap-1" onClick={() => download(`/certificates/${e.certificate_no}/pdf`, `certificate-${e.certificate_no}.pdf`)} data-testid={`cert-pdf-${e.reg_no}`}><Download size={14} />{e.certificate_no}</button>
                    ) : (
                      <button disabled={e.attendance !== "present"} className="text-xs btn-gold-outline !py-1 !px-3 disabled:opacity-40" onClick={() => act(() => api("post", `/admin/enrollments/${e.id}/certificate`), "تم إصدار الشهادة")} data-testid={`issue-cert-${e.reg_no}`}>إصدار شهادة</button>
                    )}
                  </td>
                </tr>
              ))}
            </DataTable>
          )}
        </>
      )}
    </div>
  );
}
